import { useEffect, useState } from 'react'

const GIT_SHA = (import.meta.env.VITE_GIT_SHA as string | undefined) || ''
const BUILD_TIME = (import.meta.env.VITE_BUILD_TIME as string | undefined) || ''
const BUILD_NUMBER = (import.meta.env.VITE_BUILD_NUMBER as string | undefined) || ''
const BUILD_RUN_ID = (import.meta.env.VITE_BUILD_RUN_ID as string | undefined) || ''
const REPO_URL = 'https://github.com/1michaelpalmer/assessment-engine'

function relativeTime(iso: string): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const diff = Math.max(0, Date.now() - then)
  const sec = Math.floor(diff / 1000)
  if (sec < 45) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}d ago`
  return new Date(iso).toLocaleDateString()
}

export default function VersionBadge() {
  if (!GIT_SHA && !BUILD_NUMBER) return null
  // Portal is a participant-only surface (magic-link auth), not a public lead
  // page — no gate needed. Always show so the deployed build is instantly
  // visible on every refresh.
  const short = GIT_SHA.slice(0, 7)
  const [, force] = useState(0)
  useEffect(() => {
    const t = setInterval(() => force(x => x + 1), 60_000)
    return () => clearInterval(t)
  }, [])

  // Build number is the strictly-monotonic ordering signal — every CI run
  // increments github.run_number. Higher = newer. Pairs with the SHA (clickable
  // to the commit) and relative time so a glance at the badge tells you both
  // what shipped and whether it's newer than what you saw last refresh.
  const buildLabel = BUILD_NUMBER ? `#${BUILD_NUMBER}` : ''
  const shaLabel = short ? short : ''
  const timeLabel = BUILD_TIME ? relativeTime(BUILD_TIME) : ''

  const commitHref = GIT_SHA ? `${REPO_URL}/commit/${GIT_SHA}` : REPO_URL
  const runHref = BUILD_RUN_ID ? `${REPO_URL}/actions/runs/${BUILD_RUN_ID}` : null

  const titleParts = [
    BUILD_NUMBER ? `Build #${BUILD_NUMBER}` : null,
    short ? `commit ${short}` : null,
    BUILD_TIME ? `deployed ${BUILD_TIME}` : null,
  ].filter(Boolean)

  return (
    <span
      style={{
        position: 'fixed',
        right: 12,
        bottom: 10,
        zIndex: 999,
        display: 'inline-flex',
        gap: 6,
        alignItems: 'center',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11,
        color: 'var(--muted, #8b8fa3)',
        opacity: 0.45,
        padding: '2px 6px',
        borderRadius: 4,
        background: 'transparent',
        userSelect: 'none',
        transition: 'opacity 0.15s',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLSpanElement).style.opacity = '1' }}
      onMouseLeave={e => { (e.currentTarget as HTMLSpanElement).style.opacity = '0.45' }}
      title={titleParts.join(' · ') || undefined}
    >
      {buildLabel && runHref ? (
        <>
          <a
            href={runHref}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'none' }}
            title={`Build #${BUILD_NUMBER} — view CI run`}
          >
            {buildLabel}
          </a>
          {(shaLabel || timeLabel) && <span>·</span>}
        </>
      ) : buildLabel ? (
        <>
          <span>{buildLabel}</span>
          {(shaLabel || timeLabel) && <span>·</span>}
        </>
      ) : null}
      {shaLabel && (
        <a
          href={commitHref}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'none' }}
          title={`Commit ${short} — view on GitHub`}
        >
          {shaLabel}
        </a>
      )}
      {shaLabel && timeLabel && <span>·</span>}
      {timeLabel && <span>{timeLabel}</span>}
    </span>
  )
}
